import { useEffect, useRef, useState, KeyboardEvent } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { classNameList } from 'utils/property.ts';
import styles from './Dropdown.module.scss';

interface IProps {
  id?: string;
  options: { label: string, value: string }[];
  value: string;
  onChange: (value: string) => void;
}

// TODO: disabled, error states
export const Dropdown = ({ id, options, value, onChange }: IProps) => {
  const [isOpen, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const idRef = useRef(id ?? 'dropdown' + Math.trunc(Math.random() * 100000));
  const menuDirectionRef = useRef<'up' | 'down' | null>(null);
  const selectedLabel = options.find(o => o.value === value)?.label ?? '';

  // position dropdown menu above the select if the select is in the bottom half of the viewport,
  // below otherwise.  Can be of course made more sophisticated
  useEffect(() => {
    if (isOpen && menuRef.current) {
      const viewportOffset = menuRef.current.parentElement!.getBoundingClientRect().top;
      if (viewportOffset > window.innerHeight / 2) {
        menuRef.current.style.bottom = 'calc(100% + 4px)';
        menuRef.current.style.top = '';
        menuDirectionRef.current = 'down';
      } else {
        menuRef.current.style.top = 'calc(100% + 4px)';
        menuRef.current.style.bottom = '';
        menuDirectionRef.current = 'up';
      }
    }
  }, [isOpen]);

  // close dropdown menu on a click outside dropdown
  useEffect(() => {
    const clickHandler = (e: MouseEvent) => {
      if (!menuRef.current!.parentElement!.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  }, [setOpen]);
  
  const focusOption = (value: string) => {
    document.getElementById(`${idRef.current}__${value}`)?.focus();
  };
  
  const onDropdownKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // Space or Enter on the select toggles it
    if (e.key === ' ' || e.key === 'Enter') {
      setOpen(!isOpen);
    }
    
    // Up/Down on already expanded select focuses first or last option
    // Which exactly depends on menu direction
    if (isOpen && e.key === 'ArrowUp') {
      if (menuDirectionRef.current === 'down') {
        focusOption(options[options.length - 1].value);
      } else {
        focusOption(options[0].value);
      }
      e.preventDefault();
    }
    if (isOpen && e.key === 'ArrowDown') {
      if (menuDirectionRef.current === 'down') {
        focusOption(options[0].value);
      } else {
        focusOption(options[options.length - 1].value);
      }
      e.preventDefault();
    }
  };

  const onOptionKeyDown = (value: string, e: KeyboardEvent<HTMLDivElement>) => {
    // Space or Enter on an option selects it and closes dropdown
    if (e.key === ' ' || e.key === 'Enter') {
      onChange(value);
      setOpen(false);
    }
    
    // Up/Down moves between options, wrapping around
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      const offset = e.key === 'ArrowUp' ? -1 : 1;
      const thisOptionIndex = options.findIndex(o => o.value === value);
      if (thisOptionIndex == -1) {
        return;  // shouldn't happen
      }
      let newOptionIndex = thisOptionIndex + offset;
      if (newOptionIndex < 0) {
        newOptionIndex = options.length - 1;
      }
      if (newOptionIndex >= options.length) {
        newOptionIndex = 0;
      }
      focusOption(options[newOptionIndex].value);
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <div className={styles.dropdown}
         id={idRef.current}
         tabIndex={0}     
         role="listbox"
         aria-activedescendant={`${idRef.current}__${value}`}
         onClick={() => setOpen(!isOpen)}
         onKeyDown={onDropdownKeyDown}>
      <div className={styles.dropdownSelected}>{selectedLabel}</div>
      {isOpen ? <ChevronUp /> : <ChevronDown />}
      <div className={classNameList(styles.dropdownMenu, isOpen && styles.open)} ref={menuRef}>
        {options.map(o => <div
          key={o.value}
          id={`${idRef.current}__${o.value}`}
          className={styles.dropdownOption}
          tabIndex={0}
          role="option"
          aria-selected={o.value === value ? 'true' : 'false'}
          onClick={() => onChange(o.value)}
          onKeyDown={(e) => onOptionKeyDown(o.value, e)}>
          {o.label}
        </div>)}
      </div>
    </div>
  )
  
};
